import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { StateStorage } from 'zustand/middleware'

const createLocalStorageBySiteId = (): StateStorage => ({
  getItem: (name: string): string | null => {
    if (window.location.pathname === '/appearance-preview') return null
    const siteId = localStorage.getItem('current-site-id')
    if (!siteId) {
      // SiteId is required - return null if not available
      return null
    }
    const key = `${name}_${siteId}`

    return localStorage.getItem(key)
  },
  setItem: (name: string, value: string): void => {
    if (window.location.pathname === '/appearance-preview') return
    const siteId = localStorage.getItem('current-site-id')
    if (!siteId) {
      // SiteId is required - do not save if siteId is not available
      console.warn('Cannot save to storage: siteId is required but not available')

      return
    }
    const key = `${name}_${siteId}`
    localStorage.setItem(key, value)
  },
  removeItem: (name: string): void => {
    if (window.location.pathname === '/appearance-preview') return
    const siteId = localStorage.getItem('current-site-id')
    if (!siteId) {
      // SiteId is required - do not remove if siteId is not available
      return
    }
    const key = `${name}_${siteId}`
    localStorage.removeItem(key)
  }
})

export interface PersistState {
  sessionId: string | null
  siteId: string | null
  guestId: string | null
  user: { userId: string | null; userName: string | null } | null
  token: string | null
  devMode: boolean
  isGuestIdActive: boolean
  isTakenOver: boolean
  reviewedSessions: string[]
}

interface PersistActions {
  setSessionId: (sessionId: string | null) => void
  setSiteId: (siteId: string | null) => void
  setGuestId: (guestId: string | null) => void
  setIsGuestIdActive: (isGuestIdActive: boolean) => void
  setUser: (user: { userId: string | null; userName: string | null }) => void
  setToken: (token: string | null) => void
  setDevMode: (devMode: boolean) => void
  resetStore: () => void
  setIsTakenOver: (isTakenOver: boolean) => void
  addReviewedSession: (sessionId: string) => void
}

type PersistStore = PersistState & PersistActions

const initialState: PersistState = {
  sessionId: null,
  siteId: null,
  guestId: null,
  user: null,
  token: null,
  devMode: false,
  isGuestIdActive: true,
  isTakenOver: false,
  reviewedSessions: []
}

export const usePersistStore = create<PersistStore>()(
  persist(
    set => ({
      ...initialState,

      setSessionId: sessionId => set({ sessionId }),

      setSiteId: siteId => {
        if (siteId && window.location.pathname !== '/appearance-preview')
          localStorage.setItem('current-site-id', siteId)
        set({ siteId })
      },

      setGuestId: guestId => set({ guestId }),
      setIsGuestIdActive: isGuestIdActive => set({ isGuestIdActive }),
      setUser: user => set({ user }),
      setToken: token => set({ token }),
      setDevMode: devMode => set({ devMode }),
      setIsTakenOver: isTakenOver => set({ isTakenOver }),

      addReviewedSession: sessionId =>
        set(state =>
          state.reviewedSessions.includes(sessionId)
            ? state
            : { reviewedSessions: [...state.reviewedSessions, sessionId] }
        ),

      resetStore: () =>
        set(state => ({
          ...initialState,
          siteId: state.siteId,
          guestId: state.guestId,
          devMode: state.devMode,
          reviewedSessions: state.reviewedSessions
        }))
    }),
    {
      name: 'zustand-persist-storage',

      storage: createJSONStorage(() => createLocalStorageBySiteId()),

      migrate: (persistedState: any, version: number) => {
        // Clean up old storage key without siteId
        if (typeof window !== 'undefined') {
          const oldKey = 'zustand-persist-storage'
          const oldValue = localStorage.getItem(oldKey)
          if (oldValue) {
            // Remove old key without siteId
            localStorage.removeItem(oldKey)
          }
        }

        return persistedState as any
      },

      version: 3
    }
  )
)
